import React, { useState } from 'react';
import { Layers, Download, Info, CreditCard, PiggyBank, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения и сравнения графиков платежей
 */
const PaymentSchedule = ({ 
  originalSchedule, 
  newSchedule, 
  results, 
  showFullSchedule, 
  setShowFullSchedule 
}) => {
  const [activeTab, setActiveTab] = useState('comparison');
  
  if (!originalSchedule || !newSchedule || originalSchedule.length === 0 || newSchedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого года, если не показан полный график
  const displayLimit = 24; // Показываем по 24 месяца для наглядности
  const displayOriginalSchedule = showFullSchedule ? originalSchedule : originalSchedule.slice(0, displayLimit);
  const displayNewSchedule = showFullSchedule ? newSchedule : newSchedule.slice(0, displayLimit);

  // Форматирование даты
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-green-500" />
          График платежей
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {(originalSchedule.length > displayLimit || newSchedule.length > displayLimit) && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать полностью`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('comparison')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'comparison'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Сравнение графиков
        </button>
        <button
          onClick={() => setActiveTab('original')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'original'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Оригинальный график
        </button>
        <button
          onClick={() => setActiveTab('new')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'new'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Новый график с погашением
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          {activeTab === 'comparison' && (
            <p>Сравнение оригинального и нового графика платежей. Зеленым цветом показана экономия на процентах.</p>
          )}
          {activeTab === 'original' && (
            <p>Исходный график платежей без досрочного погашения. Показано, как разделяются платежи на основной долг и проценты.</p>
          )}
          {activeTab === 'new' && (
            <p>График платежей с учетом досрочного погашения. Месяцы с досрочным погашением выделены зеленым фоном.</p>
          )}
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] payment-schedule-table">
        {/* СРАВНИТЕЛЬНАЯ ТАБЛИЦА */}
        {activeTab === 'comparison' && (
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
                <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж исходный</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж новый</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты (исх.)</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты (нов.)</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Экономия</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Досрочное</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток долга</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayNewSchedule.map((item, index) => {
                const originalItem = index < displayOriginalSchedule.length ? displayOriginalSchedule[index] : null;
                const interestSaving = originalItem ? Math.max(0, originalItem.interestPayment - item.interestPayment) : 0;
                
                return (
                  <tr
                    key={item.month}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                      ${item.earlyRepayment > 0 ? 'bg-green-50 dark:bg-green-900/20' : ''}
                      ${item.isCompleted ? 'text-gray-400 dark:text-gray-500' : ''}`}
                  >
                    <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {formatDate(item.date)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      {originalItem ? formatCurrency(originalItem.payment, 0) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      {formatCurrency(item.payment, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                      {originalItem ? formatCurrency(originalItem.interestPayment, 0) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                      {formatCurrency(item.interestPayment, 0)}
                    </td>
                    <td className={`px-3 py-1.5 whitespace-nowrap text-right ${interestSaving > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                      {interestSaving > 0 ? formatCurrency(interestSaving, 0) : '-'}
                    </td>
                    <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.earlyRepayment > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                      {item.earlyRepayment > 0 ? formatCurrency(item.earlyRepayment, 0) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.remainingBalance, 0)}
                    </td>
                  </tr>
                );
              })}

              {/* Дополнительные строки оригинального графика, если он длиннее */}
              {showFullSchedule && displayOriginalSchedule.length > displayNewSchedule.length && 
                displayOriginalSchedule.slice(displayNewSchedule.length).map((item) => (
                  <tr
                    key={`original-${item.month}`}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {formatDate(item.date)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      {formatCurrency(item.payment, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      -
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                      {formatCurrency(item.interestPayment, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                      -
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400 font-semibold">
                      {formatCurrency(item.interestPayment, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      -
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.remainingBalance, 0)}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}

        {/* ОРИГИНАЛЬНЫЙ ГРАФИК */}
        {activeTab === 'original' && (
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
                <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Ежемесячный платеж</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Основной долг</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток долга</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayOriginalSchedule.map((item) => (
                <tr
                  key={item.month}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${item.isCompleted ? 'text-gray-400 dark:text-gray-500' : ''}`}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {formatDate(item.date)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                    {formatCurrency(item.payment, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatCurrency(item.principalPayment, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.interestPayment, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                    {formatCurrency(item.remainingBalance, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {item.isCompleted ? 
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Оплачен</span> : 
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400">Планируется</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* НОВЫЙ ГРАФИК С ДОСРОЧНЫМ ПОГАШЕНИЕМ */}
        {activeTab === 'new' && (
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
                <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж по графику</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Основной долг</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Досрочное погашение</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток долга</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayNewSchedule.map((item) => (
                <tr
                  key={item.month}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${item.earlyRepayment > 0 ? 'bg-green-50 dark:bg-green-900/20' : ''}
                    ${item.isCompleted ? 'text-gray-400 dark:text-gray-500' : ''}`}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {formatDate(item.date)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                    {formatCurrency(item.payment, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatCurrency(item.principalPayment, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">
                    {formatCurrency(item.interestPayment, 0)}
                  </td>
                  <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.earlyRepayment > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                    {item.earlyRepayment > 0 ? formatCurrency(item.earlyRepayment, 0) : '-'}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                    {formatCurrency(item.remainingBalance, 0)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {item.isCompleted ? 
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Оплачен</span> : 
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400">Планируется</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && (
        (originalSchedule.length > displayLimit || newSchedule.length > displayLimit) && (
          <div className="text-center mt-4">
            <Button
              onClick={() => setShowFullSchedule(true)}
              icon={<Layers className="w-4 h-4" />}
              variant="link"
            >
              Показать весь график платежей
            </Button>
          </div>
        )
      )}

      {/* Результат сравнения графиков */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <BarChart2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Итоги сравнения графиков</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Срок кредита сократился на <span className="font-medium text-green-600 dark:text-green-400">{results.savedMonths} мес.</span> 
                {results.earlyRepaymentType === 'payment' && `, платеж уменьшился на ${formatCurrency(results.originalMonthlyPayment - results.newMonthlyPayment)}`}
                . Общая экономия составила <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(results.savedInterest)}</span>.
              </p>
            </div>
          </div>

          <Button
            onClick={() => {
              // Экспорт обычно реализуется в родительском компоненте
              const event = new CustomEvent('exportSchedule');
              document.dispatchEvent(event);
            }}
            icon={<Download className="w-4 h-4" />}
            variant="outline"
            size="sm"
          >
            Скачать график
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSchedule;